import React, { useState, useRef, ReactNode, useEffect } from 'react';
import {
  Popover,
  Checkbox,
  Chip,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  IconButton,
  Badge,
  Box,
  Button,
  Stack,
  Paper,
  Typography,
  Menu,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';

interface OptionType {
  value: string;
  label: string;
  helperText?: string;
  isState?: boolean;
  countryCode?: string;
  isExpandable?: boolean;
  isChild?: boolean;
  parentValue?: string | null;
  level?: number;
}

interface FilterDropdownProps {
  label: string;
  options: OptionType[];
  value: string[] | string;
  onChange: (value: string[] | string) => void;
  multiSelect?: boolean;
  singleSelect?: boolean;
  placeholder?: string;
  selectedCountBadge?: boolean;
  button?: ReactNode;
  hideSearch?: boolean;
  extraStyle?: React.CSSProperties;
  onNoResultsAction?: () => void;
  OnBoardingSearchBox?: any;
  setMarketSearchInfo?: (val: { searchName: string; countryCode: string } | null) => void;
  setMarketComparisons?: any;
  isFreeUser?: boolean;
  customContent?: ReactNode | ((props: { handleClose: () => void }) => ReactNode);
  rowGapSize?: 'small' | 'big';
}

// Helper function to check if all ancestors of an option are expanded
const areAncestorsExpanded = (
  option: OptionType,
  allOptions: OptionType[],
  currentExpandedParents: string[]
): boolean => {
  let currentParentValue = option.parentValue;
  while (currentParentValue) {
    if (!currentExpandedParents.includes(currentParentValue)) {
      return false;
    }
    const parentOption = allOptions.find(o => o.value === currentParentValue);
    currentParentValue = parentOption ? parentOption.parentValue : null;
  }
  return true;
};

// Helper for parent option selection
const handleParentSelect = (
  optionValue: string,
  expandedParents: string[],
  options: OptionType[],
  newValue: string[],
  newExpandedParents: string[]
) => {
  if (!expandedParents.includes(optionValue)) {
    newExpandedParents.push(optionValue);
  }
  const children = options
    .filter(o => o.isChild && o.parentValue === optionValue)
    .map(o => o.value);
  newValue = newValue.filter(v => !children.includes(v));
  if (newValue.includes(optionValue)) {
    newValue = newValue.filter(v => v !== optionValue);
  } else {
    newValue.push(optionValue);
  }
  return { newValue, newExpandedParents };
};

// Helper for child option selection
const handleChildSelect = (
  optionValue: string,
  option: OptionType,
  expandedParents: string[],
  newValue: string[],
  newExpandedParents: string[]
) => {
  newValue = newValue.filter(v => v !== option.parentValue);
  if (newValue.includes(optionValue)) {
    newValue = newValue.filter(v => v !== optionValue);
  } else {
    newValue.push(optionValue);
  }
  if (!expandedParents.includes(option.parentValue!)) {
    newExpandedParents.push(option.parentValue!);
  }
  return { newValue, newExpandedParents };
};

// Helper for regular option selection
const handleRegularSelect = (optionValue: string, newValue: string[]) => {
  if (newValue.includes(optionValue)) {
    return newValue.filter(v => v !== optionValue);
  } else {
    return [...newValue, optionValue];
  }
};

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  options,
  value,
  onChange,
  multiSelect = false,
  singleSelect = false,
  placeholder = '',
  selectedCountBadge = false,
  button,
  hideSearch = false,
  extraStyle = {},
  onNoResultsAction,
  OnBoardingSearchBox,
  setMarketSearchInfo,
  isFreeUser,
  customContent,
  rowGapSize = 'small',
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState('');
  const [expandedParents, setExpandedParents] = useState<string[]>(() => {
    const defaultExpanded: string[] = [];
    if (options && options.length > 0) {
      options.forEach(opt => {
        // Expand macros (level 0) if they are expandable
        if (opt.level === 0 && opt.isExpandable) {
          defaultExpanded.push(opt.value);
        }
        // Expand subgroups (level 1, not a direct child item like a country, and expandable)
        // In countryOptions, subgroups are level 1, !isChild, and isExpandable.
        else if (opt.level === 1 && !opt.isChild && opt.isExpandable) {
          defaultExpanded.push(opt.value);
        }
        // Specifically expand 'country-US' if it's present and expandable, to show states
        else if (opt.value === 'country-US' && opt.isExpandable) {
          if (!defaultExpanded.includes(opt.value)) {
            // Avoid duplicates if already caught by level checks
            defaultExpanded.push(opt.value);
          }
        }
      });
    }
    return defaultExpanded;
  });
  const [isMarketSearch, setIsMarketSearch] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const isLocationFilter = label === 'Location';

  const open = Boolean(anchorEl);
  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setTimeout(() => {
      inputRef.current && inputRef.current.focus();
    }, 100);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setSearch('');
    setIsMarketSearch(false);
    // Retain expanded state for US if it was expanded, allow manual collapse
    // setExpandedParents(prev => prev.filter(p => p === 'country-US' && prev.includes('country-US')));
  };

  useEffect(() => {
    if (isLocationFilter && search) {
      const searchLower = search.toLowerCase();
      // Find the 'country-US' option to get its actual value
      const usOption = options.find(opt => opt.value === 'country-US');
      if (!usOption) return;

      const usValue = usOption.value;

      // Check if any child of 'country-US' matches the search (these are the states)
      const matchesState = options.some(
        opt => opt.parentValue === usValue && opt.label.toLowerCase().includes(searchLower)
      );

      if (matchesState && !expandedParents.includes(usValue)) {
        setExpandedParents(prev => [...prev, usValue]);
      }
    }
    // When search is cleared, we don't automatically collapse 'US' here.
    // User can manually collapse it.
    // else if (isLocationFilter && !search && expandedParents.includes('country-US')) {
    //   // Optional: Collapse 'US' when search is cleared, if desired.
    //   // setExpandedParents(prev => prev.filter(p => p !== 'country-US'));
    // }
  }, [search, isLocationFilter, options, expandedParents]);

  const filteredOptions = options.filter(opt => {
    const labelLower = opt.label.toLowerCase();
    const searchLower = search.toLowerCase();

    if (!searchLower) {
      // Logic for when there's no search term
      if (opt.level === 0) {
        // Macros are always part of the list (can be collapsed/expanded)
        return true;
      }
      // For children (level > 0), check if all their ancestors are expanded
      return areAncestorsExpanded(opt, options, expandedParents);
    }

    // Search logic:
    const searchMatchesOption = labelLower.includes(searchLower);

    if (isLocationFilter) {
      const usCountryValue = options.find(o => o.value === 'country-US')?.value; // opt.value is already 'country-US' for the US itself
      const isUS = opt.value === usCountryValue;
      // A state is a child of 'country-US' if opt.parentValue === usCountryValue
      const isUSChild = opt.parentValue === usCountryValue;
      const searchMatchesUSString = 'united states'.toLowerCase().includes(searchLower);

      if (isUSChild) {
        // e.g. a state
        return (
          searchMatchesOption ||
          (searchMatchesUSString && usCountryValue && expandedParents.includes(usCountryValue))
        );
      } else if (isUS && usCountryValue) {
        // The "United States" option itself
        const searchMatchesAnyState = options.some(
          o => o.parentValue === usCountryValue && o.label.toLowerCase().includes(searchLower)
        );
        return searchMatchesOption || searchMatchesAnyState;
      }
    }
    // Default search behavior for non-Location filters or items not US/US-State
    return searchMatchesOption;
  });

  const handleExpandCollapse = (option: OptionType) => {
    if (option.isExpandable) {
      setExpandedParents(prev =>
        prev.includes(option.value)
          ? prev.filter(val => val !== option.value)
          : [...prev, option.value]
      );
    }
  };

  const handleSelect = (optionValue: string, option: OptionType) => {
    let newValue = Array.isArray(value) ? [...value] : [value];
    let newExpandedParents = [...expandedParents];

    if (option.isChild) {
      ({ newValue, newExpandedParents } = handleChildSelect(
        optionValue,
        option,
        expandedParents,
        newValue,
        newExpandedParents
      ));
    } else if (option.isExpandable) {
      ({ newValue, newExpandedParents } = handleParentSelect(
        optionValue,
        expandedParents,
        options,
        newValue,
        newExpandedParents
      ));
    } else {
      newValue = handleRegularSelect(optionValue, newValue);
    }

    setExpandedParents(newExpandedParents);
    onChange(multiSelect ? newValue : newValue[0]);
  };

  const handleDeleteChip = (chipValue: string) => {
    if (multiSelect && Array.isArray(value)) {
      onChange(value.filter(v => v !== chipValue));
    }
  };

  const selectedOptions =
    multiSelect && Array.isArray(value)
      ? options.filter(opt => value.includes(opt.value))
      : singleSelect && typeof value === 'string'
      ? options.filter(opt => opt.value === value)
      : [];

  const toggleSearchBar = () => {
    setIsMarketSearch(!isMarketSearch);
  };

  return (
    <Box sx={{ position: 'relative', display: 'inline-block', ...extraStyle }}>
      {button ? (
        <Box onClick={handleOpen} sx={{ cursor: 'pointer', position: 'relative' }}>
          {button}
        </Box>
      ) : (
        <Button
          variant="outlined"
          size="small"
          onClick={handleOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderRadius: 999,
            px: 2,
            py: 1,
            minWidth: 120,
            boxShadow: 1,
            textTransform: 'none',
            backgroundColor: 'white',
            position: 'relative',
            zIndex: 1303,
            '&:hover': { boxShadow: 3, backgroundColor: 'white' },
          }}
          endIcon={<ArrowDropDownIcon />}
        >
          <Box sx={{ mr: 1, fontWeight: 500, color: 'grey.800' }}>{label}</Box>
          {selectedCountBadge && selectedOptions.length > 0 && (
            <Badge badgeContent={selectedOptions.length} color="primary" sx={{ mr: 1 }} />
          )}
          <Stack direction="row" spacing={0.5} sx={{ maxWidth: 120, overflowX: 'auto' }}>
            {selectedOptions.map(opt => (
              <Chip
                key={opt.value}
                label={opt.label}
                size="small"
                onDelete={multiSelect ? () => handleDeleteChip(opt.value) : undefined}
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            ))}
          </Stack>
        </Button>
      )}
      <Menu
        id={`${label.toLowerCase()}-menu`}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{ style: { minWidth: anchorEl?.offsetWidth } }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {customContent ? (
          typeof customContent === 'function' ? (
            customContent({ handleClose })
          ) : (
            customContent
          )
        ) : (
          <Box sx={{ p: 2, minWidth: 250, maxWidth: 300 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>
              {label}
            </Typography>
            {!hideSearch && (
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <TextField
                  inputRef={inputRef}
                  value={search}
                  onChange={e => {
                    setSearch(e.target.value);
                  }}
                  placeholder={placeholder || `Search ${label}`}
                  size="small"
                  fullWidth
                  variant="outlined"
                  autoComplete="off"
                  onKeyDown={e => e.stopPropagation()}
                />
                <IconButton size="small" onClick={handleClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
            {multiSelect && Array.isArray(value) && selectedOptions.length > 0 && (
              <Box sx={{ my: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, overflowX: 'auto' }}>
                {!isMarketSearch &&
                  selectedOptions.map(opt => (
                    <Chip
                      key={opt.value}
                      label={opt.label}
                      size="small"
                      onDelete={() => handleDeleteChip(opt.value)}
                      sx={{ height: 'auto', '& .MuiChip-label': { py: '2px' } }}
                    />
                  ))}
              </Box>
            )}
            <List dense sx={{ maxHeight: 224, overflowY: 'auto', gap: 1 }}>
              {filteredOptions.length === 0 && search && isLocationFilter && onNoResultsAction ? (
                <Box sx={{ p: 1, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      toggleSearchBar();
                    }}
                    sx={{ my: '10px' }}
                    startIcon={<AddIcon />}
                  >
                    Add Specific{' '}
                    {isMarketSearch ? (label === 'Location' ? 'Country' : 'Region') : 'Market'}
                  </Button>
                  {!isMarketSearch && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      No matching {label === 'Location' ? 'country' : 'region'} is found for "
                      {search}".
                    </Typography>
                  )}
                </Box>
              ) : filteredOptions.length === 0 ? (
                <ListItem>
                  <ListItemText primary="No options found" />
                </ListItem>
              ) : null}
              {filteredOptions.map(opt => {
                const isSelected = multiSelect
                  ? Array.isArray(value) && value.includes(opt.value)
                  : value === opt.value;

                // Indentation based on level (default 0)
                const level = opt.level || 0;
                const paddingLeft = 2 + level * 2; // MUI spacing unit

                return (
                  <ListItemButton
                    key={opt.value}
                    dense
                    onClick={() => {
                      if (opt.isExpandable) {
                        handleExpandCollapse(opt);
                      }
                    }}
                    selected={isSelected}
                    sx={{
                      height: rowGapSize === 'big' ? '40px !important' : '30px !important',
                      pl: paddingLeft,
                      pr: opt.isExpandable ? 1 : 2,
                      ...(rowGapSize === 'big' && { marginBottom: '10px' }),
                    }}
                  >
                    {multiSelect && (
                      <ListItemIcon sx={{ minWidth: 0, mr: 1.5 }}>
                        <Checkbox
                          edge="start"
                          checked={isSelected}
                          tabIndex={-1}
                          disableRipple
                          size="small"
                          sx={{
                            padding: 0,
                            marginRight: 1,
                          }}
                          onClick={e => {
                            e.stopPropagation();
                            handleSelect(opt.value, opt);
                          }}
                        />
                      </ListItemIcon>
                    )}
                    <ListItemText primary={opt.label} secondary={opt?.helperText} />
                    {opt.isExpandable && (
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          handleExpandCollapse(opt);
                        }}
                        sx={{
                          transform: expandedParents.includes(opt.value)
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                        }}
                      >
                        {expandedParents.includes(opt.value) ? (
                          <ExpandMoreIcon />
                        ) : (
                          <ChevronRightIcon />
                        )}
                      </IconButton>
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        )}
      </Menu>
    </Box>
  );
};

export default FilterDropdown;
